import React, { useState, useEffect } from 'react';

import { useField } from 'formik';

import { DialogActions, DialogContent, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import IconButton from 'src/components/ui/IconButton';

import { colours } from 'src/constants/colours';

import useUser from 'src/hooks/useUser';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const StyledDialog = styledComponent(Dialog)`
    width: fit-content;
    margin: auto;
    .MuiPaper-root {
        padding: 1rem;
        height: 17rem;
    }
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: 100vw;
        .MuiPaper-root {
            padding: 0;
            width: 100%;
            height: 100%;
            max-height: 100%;
            margin: 0;
            border-radius: 0;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }
`;

const StyledDialogActions = styledComponent(DialogActions)`
    padding: 0.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    width: 22.75rem;
`;

const StyledFormControlLabel = styledComponent(FormControlLabel)`
    padding: 0.4rem;
    .Mui-checked {
        ~ .MuiFormControlLabel-label {
            font-weight: bold;
        }
    }
`;

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    padding: 0.2rem;
    background-color: ${colours.white.main};
`;

type Props = {
    contentType: string;
    openOwnerModal: boolean;
    onClose: (confirm: boolean) => void;
    onOwnerChosen?: () => void;
};

const ChooseOwnerModal: React.FC<Props> = ({ contentType, openOwnerModal, onClose, onOwnerChosen }) => {
    const [userIdField, userIdMeta, userIdHelpers] = useField('userId');
    const [companyIdField, companyIdMeta, companyIdHelpers] = useField('companyId');
    const [value, setValue] = useState<string | undefined>(
        (userIdField.value as string | undefined) ?? (companyIdField.value as string | undefined),
    );
    const user = useUser();
    const { t } = useTranslation(['common']);
    const { logEventDynamic } = useDynamicLogEvents();

    useEffect(() => {
        setValue((userIdField.value as string | undefined) ?? (companyIdField.value as string | undefined));
    }, [userIdField, companyIdField]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === user?.id) {
            userIdHelpers.setValue(e.target.value);
            companyIdHelpers.setValue(null);
        } else {
            companyIdHelpers.setValue(e.target.value);
            userIdHelpers.setValue(null);
        }
        setValue(e.target.value);
    };

    const handleConfirm = () => {
        setValue(user?.id);
        onClose(true);
        if (onOwnerChosen && contentType !== 'ad') {
            onOwnerChosen();
        }
        if (contentType === 'story') {
            logEventDynamic(AnalyticsEvent.Create);
        }
    };

    const closeModal = () => {
        setValue(user?.id);
        onClose(false);
    };

    return (
        <StyledDialog open={openOwnerModal} onClose={closeModal}>
            <Box>
                <CloseButton
                    data-target="choose-owner-button"
                    data-target-id="choose-owner-close"
                    color="white"
                    onClick={closeModal}
                    iconProps={{
                        name: 'Close',
                    }}
                />
                <DialogTitle>
                    <Typography data-target="choose-owner-text" data-target-id="choose-owner-title" variant="h3">
                        {t(`common:ownerModal.title.${contentType}`)}
                    </Typography>
                </DialogTitle>
                <StyledDialogContent>
                    <RadioGroup onChange={handleChange} value={value}>
                        <StyledFormControlLabel
                            data-target="choose-owner-radio"
                            data-target-id="choose-owner-user"
                            name="userId"
                            value={user?.id}
                            label={t('common:ownerModal.ownerChoice', {
                                apos: user?.fullName && /[aeiou]/i.test(user.fullName[0]) ? "'" : 'e ',
                                name: user?.fullName,
                            })}
                            control={<Radio />}
                        />
                        <StyledFormControlLabel
                            data-target="choose-owner-radio"
                            data-target-id="choose-owner-company"
                            name="companyId"
                            value={user?.affiliatedCompany?.id}
                            label={t('common:ownerModal.ownerChoice', {
                                apos: 'e ',
                                name: user?.affiliatedCompany?.name,
                            })}
                            control={<Radio />}
                        />
                    </RadioGroup>
                </StyledDialogContent>
                <StyledDialogActions>
                    <Button
                        data-target="choose-owner-button"
                        data-target-id="choose-owner-confirm"
                        onClick={handleConfirm}
                        sx={{ width: '6.25rem' }}
                    >
                        {t('common:modal.confirm')}
                    </Button>
                </StyledDialogActions>
            </Box>
        </StyledDialog>
    );
};

export default ChooseOwnerModal;
